import { Checkbox, Flex, Skeleton } from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import React from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { Card } from "src/components/Layout/Card";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { SchoolItem } from "../../types";
import { FormikType, isEnable } from "./formik";

type EligibilityCheckboxCardProps = {
  sectionType: AF.V2.SectionType;
  questionType: AF.V2.QuestionType;
  optionIndex: number;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
};
export const EligibilityCheckboxCard: React.FC<
  EligibilityCheckboxCardProps
> = ({ sectionType, questionType, schools: schoolsRD, optionIndex }) => {
  const typeName = `options.${optionIndex}.eligibility.type`;
  const [typeField, , typeHelper] = useField(typeName);

  const schoolIdsName = `options.${optionIndex}.eligibility.schoolIds`;
  const [schoolIdsField, schoolIdsMeta, schoolIdsHelper] =
    useField(schoolIdsName);

  const eligibility = {
    type: typeField.value ?? "disabled",
    schoolIds: schoolIdsField.value ?? [],
  };

  const initialValues = React.useMemo(() => {
    if (!schoolsRD.hasData()) {
      return [];
    }

    return eligibility.schoolIds.flatMap((schoolId: string) => {
      const school = schoolsRD.data.find((school) => school.id === schoolId);
      if (school) {
        return [school];
      } else {
        return [{ id: schoolId, label: schoolId }];
      }
    });
  }, [eligibility.schoolIds, schoolsRD]);

  const formik = useFormikContext<FormikType>();

  if (sectionType !== "PreRankingSection" || questionType !== "SingleSelect") {
    return null;
  }

  return (
    <Flex direction="column" gap="2">
      <Card showBorder padding="2">
        <Checkbox
          isChecked={isEnable(eligibility)}
          onChange={async () => {
            await typeHelper.setValue(
              isEnable(eligibility) ? "disabled" : "excludes"
            );
            await typeHelper.setTouched(true);
          }}
        >
          Answer excludes specific schools
        </Checkbox>
      </Card>
      {isEnable(eligibility) ? (
        <RemoteDataView
          remoteData={schoolsRD}
          error={() => <GenericError />}
          loading={
            <Flex direction="column" gap="3">
              <Skeleton height="16" />
              <Skeleton height="10" width="20" />
            </Flex>
          }
        >
          {(schools) => {
            return (
              <Flex direction="column" gap="2">
                <MultiSelectInput
                  validateSelectedItems
                  onChange={async (selectedSchools) => {
                    await schoolIdsHelper.setValue(
                      selectedSchools.map((s) => s.id)
                    );
                    await schoolIdsHelper.setTouched(true);
                  }}
                  label="Select schools"
                  size="md"
                  options={schools}
                  initialValues={initialValues}
                  error={
                    // Show error when the field is touched, or when user submit the form
                    (schoolIdsMeta.touched || formik.submitCount > 0) &&
                    schoolIdsMeta.error
                      ? schoolIdsMeta.error
                      : undefined
                  }
                />
              </Flex>
            );
          }}
        </RemoteDataView>
      ) : null}
    </Flex>
  );
};
